import { 
  Box, 
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
  Tooltip,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';

import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { IMultiSectionUnitConfiguration } from "../../../../Models/IUnitConfiguration";
import { useMsal } from "@azure/msal-react";
import { ApiService } from "../../../../Services/ApiService";
import { ICalculationGroup, ICalculationInput, ICalculationOutput, IUnitPropertySpecification } from "../../../../Models/IUnitPropertySpecification";
import { setTemporaryState } from "../../../../Services/Global";
import { MultiSectionFlowchart } from "../../../Atom/MultiSectionVisualization/MultiSectionFlowchart";
import logger from '../../../../Services/LoggingService';
import { createFlexStyles, createSpacingStyles } from '../../../../theme/layoutStyles';

type InputOutputFieldType = "input" | "output";

/**
 * Unit property specification editor component to manage the unit property specifications using Material UI
 *
 * @return {*} 
 */
export const UnitPropertySpecificationEditor: React.FC = () => {
    const ctx = useMsal();
    const apiService = useMemo(() => new ApiService(ctx), [ctx]);

    const navigate = useNavigate();
    const params = useParams();
    const theme = useTheme();
    const flexStyles = createFlexStyles(theme);
    const spacingStyles = createSpacingStyles(theme);

    const [unitConfig] = useState<IMultiSectionUnitConfiguration>({
        industry: params.i,
        subIndustry: params.si,
        application: params.a,
        subApplication: params.sa,
        processStage: params.ps,
        productLine: params.pl,
        alfaLavalPosition: params.alp,
        sectionCount: Number.parseInt(params.sc as string)
    });
    const [pickableProperties, setPickableProperties] = useState<string[]>([]);
    const [selectedProperty, setSelectedProperty] = useState<string>();
    const [copyMessage, setCopyMessage] = useState<string>("");
    const [specifications, setSpecifications] = useState<ICalculationGroup[]>([]);
    const [specId, setSpecId] = useState<string>();
    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState<string>();
    const [showAllProperties, setShowAllProperties] = useState<boolean>(false);

    const [userInputs, setUserInputs] = useState<string[]>([]);

    // Styles based on UnitConfigurationManagement and UnitConfigurationEditor
    const styles = {
        container: {
            padding: theme.spacing(3),
            marginTop: theme.spacing(2),
        },
        contentWrapper: {
            width: '100%',
            maxWidth: '98%',
            margin: '0 auto',
        },
        header: {
            ...flexStyles.flexCenterBetween,
            width: '100%',
            ...spacingStyles.marginBottom3
        },
        section: {
            padding: theme.spacing(3),
            ...spacingStyles.marginBottom3,
            backgroundColor: theme.palette.background.paper,
            borderRadius: theme.shape.borderRadius,
            boxShadow: theme.shadows[1],
        },
        formRow: {
            ...flexStyles.flexCenter,
            alignItems: 'flex-end', // Override center alignment
            gap: theme.spacing(2),
            ...spacingStyles.marginBottom2
        },
        fieldContainer: {
            flex: 1,
            minWidth: '200px',
        },
        chipContainer: {
            ...flexStyles.flexCenter,
            flexWrap: 'wrap',
            gap: theme.spacing(1),
            marginTop: theme.spacing(1),
            ...spacingStyles.marginBottom2
        },
        calculationGroup: {
            padding: theme.spacing(3),
            ...spacingStyles.marginBottom2,
            backgroundColor: theme.palette.background.paper,
            borderRadius: theme.shape.borderRadius,
            boxShadow: theme.shadows[1],
            borderLeft: `5px solid ${theme.palette.primary.main}`,
        },
        inputOutputSection: {
            padding: theme.spacing(3),
            marginTop: theme.spacing(2),
            ...spacingStyles.marginBottom2,
            backgroundColor: theme.palette.grey[50],
            borderRadius: theme.shape.borderRadius,
            boxShadow: 'none',
            borderLeft: `5px solid ${theme.palette.secondary.light}`,
        },
        codeReference: {
            ...flexStyles.flexCenter,
            padding: theme.spacing(1),
            backgroundColor: theme.palette.grey[100],
            borderRadius: theme.shape.borderRadius,
            marginTop: theme.spacing(1)
        },
        code: {
            fontFamily: 'monospace',
            marginRight: theme.spacing(1),
        },
        copySuccess: {
            ...flexStyles.flexCenter,
            color: theme.palette.success.main,
            marginLeft: theme.spacing(1)
        }
    };

    useEffect(() => {
        async function getFullUnitConfiguration() {
            let configs: IMultiSectionUnitConfiguration[] = [];
            if (unitConfig.sectionCount === 1) {
                if (!state.unitConfiguration) {
                    const response = await apiService.getAsync("settings/unitconfiguration", unitConfig);
                    const data = await response.json();
                    configs = [data];
                }
                else {
                    configs = [state.unitConfiguration];
                }
            }
            else {
                const response = await apiService.getAsync("settings/unitconfigurations/sections", unitConfig);
                const data = await response.json();
                configs = data;
            }

            let allPickables: string[] = ["UnitOperationTargetValue"];
            configs.forEach((c, index) => {
                c.inputs?.forEach((i) => {
                    allPickables.push(`${i}Sec${index + 1}`);
                });

                c.outputs?.forEach((o) => {
                    allPickables.push(`${o}Sec${index + 1}`);
                });
            });
            setPickableProperties(allPickables);
        }

        /**
         * Get the unit property specification from the API and set the state with the result
         *
         * @return {*} 
         */
        async function getUnitPropertySpecification() {
            const response = await apiService.getAsync("settings/unitpropertyspecifications", unitConfig);
            if (!response.ok) {
                if (response.status === 404) {
                    return;
                }

                throw new Error(`${response.status}`);
            }

            try {
                const result: IUnitPropertySpecification = await response.json();

                if (result) {
                    setSpecifications(result.calculations);
                    setSpecId(result.id)
                    setUserInputs(result.userInputProperties ?? []);
                }
            }
            catch {
                logger.warn('error while parsing json of unit property specification');
            }
        }

        getFullUnitConfiguration();
        getUnitPropertySpecification();
    }, [])

    /**
     * Add a new calculation group to the specifications state array if a property is selected from the dropdown
     *
     * @return {*} 
     */
    const addNewCalculationGroup = () => {
        if (!selectedProperty) return;

        let currentSpecifications = [...specifications];
        let target = currentSpecifications.find((g) => g.name === selectedProperty);
        if (!target) {
            target = {
                name: selectedProperty,
                inputs: [],
                outputs: []
            };
            currentSpecifications.push(target);
        }

        setSpecifications([...currentSpecifications]);
    }

    const handleDropdownChange = (value: string | null) => {
        setSelectedProperty(value || undefined);
    }

    /**
     * Handle adding a new field to the calculation group based on the input type (input or output) and the key of the group to add the field to the correct group in the state
     *
     * @param {ICalculationGroup} spec
     * @param {number} key
     * @param {InputOutputFieldType} inputType
     */
    const handleAddField = (spec: ICalculationGroup, key: number, inputType: InputOutputFieldType) => {
        let current = specifications;
        let index = current.findIndex(x => x.name === spec.name);
        if (inputType === "input") {
            current[index].inputs?.push({
                data: "",
                unit: "",
                name: "",
                key: current[index].inputs.length,
                groupRef: spec.name
            });
        }
        else {
            current[index].outputs?.push({
                calculation: "",
                unit: "",
                name: "",
                key: 0, // only one allowed
                groupRef: spec.name
            });
        }

        setSpecifications([...current]);
    }

    /**
     * Handle copying the content to the clipboard and set a temporary state to show the user that the content was copied successfully 
     *
     * @param {string} content
     */
    function handleCopy(content: string) {
        navigator.clipboard.writeText(content);
        setTemporaryState(content, setCopyMessage);
    }

    /**
     * Handle the input data and update the state with the new data for the input field in the calculation group based on the index, group name and property to update 
     *
     * @param {string} data
     * @param {number} index
     * @param {string} groupName
     * @param {string} property
     * @return {*}  {void}
     */
    function handleInput(data: string, index: number, groupName: string, property: string): void {
        let current = specifications.find((s) => s.name === groupName)?.inputs;
        if (current === undefined) return;

        let target = current.find(x => x.groupRef === groupName && x.key === index);
        if (target === undefined) {
            target = {
                data: data,
                key: index,
                unit: "",
                groupRef: groupName,
                name: ""
            }

            current.push(target);
        }

        target = { ...target, [property]: data };

        current[current.findIndex(x => x.groupRef === groupName && x.key === index)!] = target;

        let currentSpec = specifications.find((s) => s.name === groupName)!;
        currentSpec.inputs = current;
        let updatedSpecs = specifications;
        updatedSpecs[specifications.findIndex(x => x.name === groupName)!] = currentSpec;
        setSpecifications([...updatedSpecs]);
    }

    function handleOutput(data: string, index: number, groupName: string, property: string): void {
        let current = specifications.find((s) => s.name === groupName)?.outputs;
        if (current === undefined) return;

        let target = current.find(x => x.groupRef === groupName && x.key === index);

        if (target === undefined) {
            target = {
                calculation: "",
                key: index,
                unit: "",
                groupRef: groupName,
                name: ""
            }

            current.push(target);
        }

        target = { ...target, [property]: data };
        current[current.findIndex(x => x.groupRef === groupName && x.key === index)!] = target;

        let currentSpec = specifications.find((s) => s.name === groupName)!;
        currentSpec.outputs = current;
        let updatedSpecs = specifications;
        updatedSpecs[specifications.findIndex(x => x.name === groupName)!] = currentSpec;
        setSpecifications([...updatedSpecs]);
    }

    /**
     * Handle adding a new field to the calculation group based on the input type (input or output) and the key of the group to add the field to the correct group in the state 
     *
     */
    const handleSubmit = async () => {
        setIsSaving(true);
        let payload: any = {
            ...unitConfig,
            calculations: specifications,
            userInputProperties: userInputs
        }
        let response;
        if (specId) {
            payload.id = specId;
            response = await apiService.putAsync(payload, `settings/unitPropertySpecifications/${specId}`);
        }
        else {
            response = await apiService.postAsync(payload, "settings/unitPropertySpecifications");
        }

        if (!response.ok) {
            setIsSaving(false);
            setTemporaryState("Failed to save.", setSaveMessage);
            const message = await response.text();
            throw new Error(`${message} (${response.status})`);
        }

        const result: IUnitPropertySpecification = await response.json();
        setSpecifications(result.calculations);
        setSpecId(result.id);
        setTemporaryState("✅ Saved", setSaveMessage);
        setIsSaving(false);
    }

    /**
     * To remove a field from the calculation group based on the index, group name and type of field to remove
     *
     * @param {number} index
     * @param {string} group
     * @param {InputOutputFieldType} type
     * @return {*} 
     */
    const handleRemoveField = (index: number, group: string, type: InputOutputFieldType) => {
        let target = specifications.find(x => x.name === group);
        if (!target) return;

        if (type === "input") {
            target?.inputs.splice(index, 1);
        }
        else {
            target?.outputs.splice(index, 1);
        }

        let updatedSpecs = specifications;
        updatedSpecs[specifications.findIndex(x => x.name === group)!] = target;
        setSpecifications([...updatedSpecs]);
    }

    /**
     * Handle removing a specification group based on the index of the group to remove from the state array of specifications 
     *
     * @param {number} index
     * @return {*} 
     */
    const handleRemoveSpecification = (index: number) => {
        let target = specifications[index];
        if (!target) return;

        let updatedSpecs = specifications;
        updatedSpecs.splice(index, 1);
        setSpecifications([...updatedSpecs]);
    }

    /**
     * Add a user input to the state array of user inputs if it does not already exist
     *
     * @param {string} value
     */
    const addUserInput = (value: string) => {
        let currentInputs = userInputs;
        if (!currentInputs) {
            currentInputs = [];
        }
        if (currentInputs.findIndex(x => x === value) === -1) {
            currentInputs.push(value);
        }

        setUserInputs([...currentInputs]);
    }

    /**
     * Remove a user input from the state array of user inputs if it exists
     *
     * @param {string} value
     */
    const removeUserInput = (value: string) => {
        let currentInputs = userInputs;
        if (!currentInputs) {
            currentInputs = [];
        }
        if (currentInputs.findIndex(x => x === value) !== -1) {
            currentInputs.splice(currentInputs.findIndex(x => x === value), 1);
        }

        setUserInputs([...currentInputs]);
    }
    
    const state: any = useLocation().state;
    const handleNavigation = () => {
        if (state && state.unitConfiguration) {
            navigate('/admin/UnitConfigurationManagement/unitconfigurationeditor', { state: { unitConfiguration: state.unitConfiguration } });
        }
        else {
            navigate(-1);
        }
    };

    // Gets the color for user input chips based on name
    const getChipColor = (name: string) => {
        return name.toLowerCase().includes('hot') ? 'error' : 'primary';
    };

    return (
        <Box sx={styles.container}>
            <Stack spacing={3} sx={styles.contentWrapper}>
                <Box sx={styles.header}>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={handleNavigation}
                    >
                        Back
                    </Button>
                    <Box sx={flexStyles.flexCenter}>
                        {isSaving && (
                            <Box sx={{ ...flexStyles.flexCenter, mr: 2 }}>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                <Typography variant="body2">Saving...</Typography>
                            </Box>
                        )}
                        {saveMessage && (
                            <Typography variant="body2" sx={{ mr: 2 }}>
                                {saveMessage}
                            </Typography>
                        )}
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSubmit}
                            disabled={isSaving}
                        >
                            Save all information
                        </Button>
                    </Box>
                </Box>

                <Stack alignItems="center">
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                        Unit Property Specification
                    </Typography>
                    <Box sx={{ ...flexStyles.flexCenter, justifyContent: 'center', width: '100%', mb: 3 }}>
                        <MultiSectionFlowchart
                            unitInputConfiguration={unitConfig}
                            sectionCount={unitConfig.sectionCount ?? 1}
                            selectableProperties={[]}
                            scaling={0.9}
                        />
                    </Box>
                </Stack>

                <Paper sx={styles.section}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                        Assign User Inputs
                    </Typography>
                    
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            Assigned User Inputs
                        </Typography>
                        <Box sx={styles.chipContainer}>
                            {userInputs?.map((x, index) => (
                                <Chip
                                    key={`userinput-${index}`}
                                    label={x}
                                    color={getChipColor(x)}
                                    onDelete={() => removeUserInput(x)}
                                    sx={{ p: 1 }}
                                />
                            ))}
                            {(!userInputs || userInputs.length === 0) && (
                                <Typography variant="body2" color="text.secondary">
                                    No user inputs assigned. Click on available properties below to assign.
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    
                    <Box>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            Available Properties
                        </Typography>
                        <Box sx={styles.chipContainer}>
                            {pickableProperties
                                ?.filter(x => !userInputs?.includes(x))
                                .map((x, index) => (
                                    <Chip
                                        key={`selectable-user-input-${index}`}
                                        label={x}
                                        color={getChipColor(x)}
                                        variant="outlined"
                                        onClick={() => addUserInput(x)}
                                        sx={{ p: 1 }}
                                    />
                                ))}
                            {(!pickableProperties || pickableProperties.length === 0) && (
                                <Typography variant="body2" color="text.secondary">
                                    No properties available.
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Paper>

                <Paper sx={styles.section}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                        Specifications
                    </Typography>
                    
                    <Box sx={styles.formRow}>
                        <Box sx={styles.fieldContainer}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="available-properties-label">Available properties</InputLabel>
                                <Select
                                    labelId="available-properties-label"
                                    label="Available properties"
                                    value={selectedProperty || ""}
                                    onChange={(e) => handleDropdownChange(e.target.value)}
                                >
                                    {(showAllProperties
                                        ? pickableProperties
                                        : pickableProperties?.filter(x => userInputs?.includes(x))
                                    )?.map((prop) => (
                                        <MenuItem key={prop} value={prop}>{prop}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showAllProperties}
                                    onChange={() => setShowAllProperties(!showAllProperties)}
                                />
                            }
                            label="Show all properties"
                        />
                    </Box>
                    
                    <Box sx={{ mt: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={addNewCalculationGroup}
                            disabled={!selectedProperty}
                        >
                            Add new specification
                        </Button>
                    </Box>
                    
                    <Box sx={{ mt: 2 }}>
                        <Alert severity="warning">
                            If a group is dependent on an output from an upstream group, they need to be added subsequentially.
                        </Alert>
                    </Box>

                    {specifications && specifications.map((spec, index) => (
                        <Paper key={`spec-${index}`} sx={{
                            ...styles.calculationGroup,
                            borderLeft: `5px solid ${spec.name.toLowerCase().includes("hot") 
                                ? theme.palette.error.main 
                                : theme.palette.primary.main}`
                        }}>
                            <Box sx={{ ...flexStyles.flexCenterBetween, mb: 2 }}>
                                <Typography variant="h6" fontWeight="bold" color={spec.name.toLowerCase().includes("hot") 
                                    ? "error.main" 
                                    : "primary.main"}>
                                    {spec.name}
                                </Typography>
                                <Tooltip title="Delete group">
                                    <IconButton 
                                        color="error" 
                                        onClick={() => handleRemoveSpecification(index)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            <Paper sx={styles.inputOutputSection}>
                                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                    Inputs
                                </Typography>
                                
                                {spec.inputs?.map((input, ind) => (
                                    <Box key={`group-${index}-input-${ind}`} sx={{ 
                                        ...flexStyles.flexCenter,
                                        alignItems: 'flex-end', // Override center alignment
                                        gap: 2, 
                                        mb: 2,
                                        flexWrap: { xs: 'wrap', md: 'nowrap' }
                                    }}>
                                        <FormControl sx={{ flex: 1, minWidth: '180px' }} size="small">
                                            <InputLabel id={`property-select-${index}-${ind}-label`}>Pick from properties</InputLabel>
                                            <Select
                                                labelId={`property-select-${index}-${ind}-label`}
                                                label="Pick from properties"
                                                value={input.name || ""}
                                                onChange={(e) => handleInput(e.target.value, ind, spec.name, "name")}
                                            >
                                                {pickableProperties?.map((prop) => (
                                                    <MenuItem key={prop} value={prop}>{prop}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        
                                        <TextField 
                                            label="Field name"
                                            size="small"
                                            value={input.name || ""}
                                            onChange={(e) => handleInput(e.target.value, ind, spec.name, "name")}
                                            sx={{ flex: 1, minWidth: '180px' }}
                                        />
                                        
                                        <Box sx={{ 
                                            ...flexStyles.flexCenter,
                                            flex: 1, 
                                            backgroundColor: theme.palette.grey[100],
                                            borderRadius: theme.shape.borderRadius,
                                            p: 1
                                        }}>
                                            <Typography variant="body2" fontFamily="monospace" sx={{ mr: 1 }}>
                                                {`group_${index}_input_${ind}`}
                                            </Typography>
                                            <Tooltip title="Copy">
                                                <IconButton 
                                                    size="small"
                                                    onClick={() => handleCopy(`group_${index}_input_${ind}`)}
                                                >
                                                    <ContentCopyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {copyMessage === `group_${index}_input_${ind}` && (
                                                <Box sx={{ ...flexStyles.flexCenter, color: 'success.main', ml: 1 }}>
                                                    <CheckIcon fontSize="small" />
                                                    <Typography variant="caption">Copied!</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                        
                                        <Tooltip title={`Delete input ${input.name || ""}`}>
                                            <IconButton 
                                                color="error" 
                                                onClick={() => handleRemoveField(ind, spec.name, "input")}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                ))}
                                
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleAddField(spec, index, "input")}
                                    sx={{ mt: 1 }}
                                >
                                    New input
                                </Button>
                            </Paper>

                            <Paper sx={styles.inputOutputSection}>
                                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                    Outputs
                                </Typography>
                                
                                {spec.outputs?.map((output, ind) => (
                                    <Box key={`group-${index}-output-${ind}`} sx={{ 
                                        ...flexStyles.flexCenter,
                                        alignItems: 'flex-end', // Override center alignment
                                        gap: 2, 
                                        mb: 2,
                                        flexWrap: { xs: 'wrap', md: 'nowrap' }
                                    }}>
                                        <TextField
                                            label={`Calculation (${spec.name})`}
                                            size="small"
                                            fullWidth
                                            value={output.calculation || ""}
                                            onChange={(e) => handleOutput(e.target.value, 0, spec.name, "calculation")}
                                            sx={{ flex: 3, minWidth: '200px' }}
                                        />
                                        
                                        <TextField
                                            label="Unit"
                                            size="small"
                                            value={output.unit || ""}
                                            onChange={(e) => handleOutput(e.target.value, 0, spec.name, "unit")}
                                            sx={{ flex: 1, minWidth: '100px' }}
                                        />
                                        
                                        <Box sx={{ 
                                            ...flexStyles.flexCenter,
                                            flex: 1, 
                                            backgroundColor: theme.palette.grey[100],
                                            borderRadius: theme.shape.borderRadius,
                                            p: 1
                                        }}>
                                            <Typography variant="body2" fontFamily="monospace" sx={{ mr: 1 }}>
                                                {`group_${index}_output_${ind}`}
                                            </Typography>
                                            <Tooltip title="Copy">
                                                <IconButton 
                                                    size="small"
                                                    onClick={() => handleCopy(`group_${index}_output_${ind}`)}
                                                >
                                                    <ContentCopyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {copyMessage === `group_${index}_output_${ind}` && (
                                                <Box sx={{ ...flexStyles.flexCenter, color: 'success.main', ml: 1 }}>
                                                    <CheckIcon fontSize="small" />
                                                    <Typography variant="caption">Copied!</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                        
                                        <Tooltip title={`Delete output ${output.name || ""}`}>
                                            <IconButton 
                                                color="error" 
                                                onClick={() => handleRemoveField(ind, spec.name, "output")}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                ))}
                                
                                {spec.outputs?.length < 1 && (
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={() => handleAddField(spec, index, "output")}
                                        sx={{ mt: 1 }}
                                    >
                                        New output
                                    </Button>
                                )}
                            </Paper>
                        </Paper>
                    ))}
                </Paper>
            </Stack>
        </Box>
    );
} 